/**
 * The formatting a procedure is actually written with, beyond what StarterKit brings.
 *
 * WRITTEN HERE RATHER THAN INSTALLED. Superscript and subscript are ten lines each; an indent and
 * the space above and below a paragraph are one shared attribute each. Four more dependencies to
 * carry, version and audit, for sixty lines, is a bad trade in an application whose whole point is
 * that the document format is ours and stable.
 *
 * INDENT AND SPACING ARE ATTRIBUTES ON THE BLOCK, not marks and not CSS classes. A controlled
 * document is stored as JSON and rendered twice -- here and by the server-side rendition that
 * produces the PDF people sign -- so anything the author can see has to be IN the document, in a
 * form the renderer can read. A class name would be a piece of this stylesheet that the renderer
 * has never heard of.
 */
import { Extension, Mark, mergeAttributes } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";

export const Superscript = Mark.create({
    name: "superscript",
    // EXCLUSIVE OF ITS OPPOSITE: text is above the line or below it, and a mark that is both is a
    // document nobody can render.
    excludes: "superscript subscript",
    parseHTML() { return [{ tag: "sup" }, { style: "vertical-align=super" }]; },
    renderHTML({ HTMLAttributes }) { return ["sup", mergeAttributes(HTMLAttributes), 0]; },
});

export const Subscript = Mark.create({
    name: "subscript",
    excludes: "superscript subscript",
    parseHTML() { return [{ tag: "sub" }, { style: "vertical-align=sub" }]; },
    renderHTML({ HTMLAttributes }) { return ["sub", mergeAttributes(HTMLAttributes), 0]; },
});

/** How far one press of Increase indent moves a paragraph. Google's is half an inch. */
export const INDENT_STEP = 0.5;
export const MAX_INDENT = 4;

const number = (value: any) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Indent, space above and space below, on every block that can carry them.
 *
 * ONE EXTENSION FOR THE THREE because they are the same mechanism: a number on the node, written
 * into the style attribute on the way out and read back off it on the way in -- which is also what
 * makes a document pasted FROM this editor keep its shape.
 */
export const BlockSpacing = Extension.create({
    name: "blockSpacing",

    addGlobalAttributes() {
        return [{
            types: ["paragraph", "heading", "blockquote", "bulletList", "orderedList"],
            attributes: {
                indent: {
                    default: null,
                    parseHTML: (element) => number(element.style.marginLeft) || null,
                    renderHTML: (attributes) => attributes.indent
                        ? { style: `margin-left: ${attributes.indent}in` } : {},
                },
                // THE RIGHT EDGE OF THE PARAGRAPH, which a quotation pulls in from both sides and
                // which nothing on the toolbar could reach before the ruler had a marker for it.
                indentRight: {
                    default: null,
                    parseHTML: (element) => number(element.style.marginRight) || null,
                    renderHTML: (attributes) => attributes.indentRight
                        ? { style: `margin-right: ${attributes.indentRight}in` } : {},
                },
                // THE FIRST LINE ONLY, and it may be NEGATIVE -- which is the whole reason it is
                // its own attribute rather than a special case of indent. A positive value is the
                // ordinary paragraph indent; a negative one is a HANGING indent, where the first
                // line starts left of the rest, which is how a numbered clause in a procedure is
                // written and what the left marker dragging under the first-line marker means.
                firstLine: {
                    default: null,
                    parseHTML: (element) => number(element.style.textIndent) || null,
                    renderHTML: (attributes) => attributes.firstLine
                        ? { style: `text-indent: ${attributes.firstLine}in` } : {},
                },
                // TAB STOPS, as a comma-separated list of inches from the text margin.
                //
                // A DATA ATTRIBUTE, NOT A STYLE, because CSS has no way to say "a tab stop at 2.5
                // inches": `tab-size` is one width repeated, which is the thing tab stops exist to
                // replace. So the positions are carried on the element and the editor does the
                // spacing itself -- see the TabStops extension, and the rendition, which has to
                // draw them the same way or a signed PDF stops matching the screen.
                tabStops: {
                    default: null,
                    parseHTML: (element) => {
                        const raw = element.getAttribute("data-tab-stops");
                        if (!raw) return null;
                        const stops = raw.split(",").map(parseFloat).filter(Number.isFinite);
                        return stops.length ? stops : null;
                    },
                    renderHTML: (attributes) => attributes.tabStops?.length
                        ? { "data-tab-stops": attributes.tabStops.join(",") } : {},
                },
                spaceBefore: {
                    default: null,
                    parseHTML: (element) => number(element.style.marginTop) || null,
                    renderHTML: (attributes) => attributes.spaceBefore
                        ? { style: `margin-top: ${attributes.spaceBefore}pt` } : {},
                },
                spaceAfter: {
                    default: null,
                    parseHTML: (element) => number(element.style.marginBottom) || null,
                    renderHTML: (attributes) => attributes.spaceAfter
                        ? { style: `margin-bottom: ${attributes.spaceAfter}pt` } : {},
                },
            },
        }];
    },
});

/** The blocks the caret is in or across, as {pos, node} -- what every command below acts on. */
function selectedBlocks(editor: any) {
    const { state } = editor;
    const { from, to } = state.selection;
    const found: Array<{ pos: number; node: any }> = [];
    state.doc.nodesBetween(from, to, (node: any, pos: number) => {
        if (node.isTextblock || ["bulletList", "orderedList", "blockquote"].includes(node.type.name)) {
            found.push({ pos, node });
        }
    });
    return found;
}

function setBlockAttribute(editor: any, name: string, value: (current: any) => any) {
    if (!editor) return;
    const { state, view } = editor;
    const tr = state.tr;
    for (const { pos, node } of selectedBlocks(editor)) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, [name]: value(node.attrs[name]) });
    }
    if (tr.docChanged) view.dispatch(tr);
    view.focus();
}

export function indentBlocks(editor: any, direction: 1 | -1) {
    setBlockAttribute(editor, "indent", (current) => {
        const next = Math.min(MAX_INDENT, Math.max(0, (current || 0) + direction * INDENT_STEP));
        return next || null;              // null rather than 0, so the attribute disappears again
    });
}

export const indentOf = (editor: any) =>
    (selectedBlocks(editor)[0]?.node.attrs.indent as number | null) ?? 0;

/**
 * The three horizontal measurements of the block the caret is in, in inches from the text margin.
 *
 * READ FROM THE FIRST BLOCK IN THE SELECTION, as every other reader here does: a selection spanning
 * paragraphs with different indents has no single answer, and showing the first one's is what a
 * word processor does -- the markers then APPLY to all of them, which is also what it does.
 */
export function blockIndents(editor: any): {indent: number; firstLine: number; indentRight: number} {
    const attrs = selectedBlocks(editor)[0]?.node.attrs ?? {};
    return {
        indent: (attrs.indent as number) || 0,
        firstLine: (attrs.firstLine as number) || 0,
        indentRight: (attrs.indentRight as number) || 0,
    };
}

/** Write one of them. Zero clears the attribute rather than storing a zero nobody meant. */
export function setBlockIndent(editor: any, which: "indent" | "firstLine" | "indentRight",
                               inches: number) {
    setBlockAttribute(editor, which, () => (Math.abs(inches) < 0.01 ? null : inches));
}

/** The tab stops on the block the caret is in, in inches from the text margin, ascending. */
export function tabStopsOf(editor: any): number[] {
    const stops = selectedBlocks(editor)[0]?.node.attrs.tabStops;
    return Array.isArray(stops) ? [...stops].sort((a, b) => a - b) : [];
}

/** Replace them. An empty list clears the attribute rather than storing an empty one. */
export function setTabStops(editor: any, stops: number[]) {
    setBlockAttribute(editor, "tabStops", () => (stops.length ? [...stops].sort((a, b) => a - b) : null));
}

export function setParagraphSpace(editor: any, which: "spaceBefore" | "spaceAfter", points: number | null) {
    setBlockAttribute(editor, which, () => points);
}

export const paragraphSpace = (editor: any, which: "spaceBefore" | "spaceAfter") =>
    (selectedBlocks(editor)[0]?.node.attrs[which] as number | null) ?? null;

/**
 * Change the case of what is selected.
 *
 * REWRITTEN IN PLACE, text node by text node and BACKWARDS, because every replacement shifts the
 * positions after it and the easy forward loop corrupts the second one onwards. Marks survive: an
 * insertion at a position inherits the marks there, so a bold word stays bold in capitals.
 */
export function setCase(editor: any, mode: "lower" | "upper" | "title") {
    if (!editor) return;
    const { state, view } = editor;
    const { from, to } = state.selection;
    if (from === to) return;

    const transform = (text: string) => mode === "lower" ? text.toLowerCase()
        : mode === "upper" ? text.toUpperCase()
        : text.replace(/\w\S*/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase());

    const pieces: Array<{ from: number; to: number; text: string }> = [];
    state.doc.nodesBetween(from, to, (node: any, pos: number) => {
        if (!node.isText) return;
        const start = Math.max(from, pos);
        const end = Math.min(to, pos + node.nodeSize);
        if (end > start) pieces.push({ from: start, to: end, text: node.text.slice(start - pos, end - pos) });
    });

    const tr = state.tr;
    for (const piece of pieces.reverse()) {
        const next = transform(piece.text);
        if (next !== piece.text) tr.insertText(next, piece.from, piece.to);
    }
    if (tr.docChanged) view.dispatch(tr);
    view.focus();
}

/** The table the caret is in, as {pos, node}, or null. */
function tableAround(editor: any): { pos: number; node: any } | null {
    const { $from } = editor?.state?.selection ?? {};
    if (!$from) return null;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.type.name === "table") return { pos: $from.before(depth), node };
    }
    return null;
}

/** Whether this table's first row is already a title row -- what "Unpin" acts on. */
export function hasTitleRow(editor: any): boolean {
    const table = tableAround(editor);
    const first = table?.node.firstChild;
    if (!first) return false;
    let header = false;
    first.forEach((cell: any) => { if (cell.type.name === "tableHeader") header = true; });
    return header;
}

/**
 * Insert a title row, as Google's menu means it: a NEW row at the top, made the header.
 *
 * NOT `toggleHeaderRow` ON ITS OWN, which converts the row that is already there -- so a table whose
 * first row is data would silently lose it into a heading, and a table that already had a title row
 * would have it turned back into data by the item that claims to add one.
 */
export function insertTitleRow(editor: any) {
    if (!editor) return;
    const already = hasTitleRow(editor);
    const table = tableAround(editor);

    // THE NEW ROW GOES AT THE TOP, which is where a title row is -- so the caret moves to the first
    // cell first. addRowBefore adds above the row the caret is in, and the caret may be anywhere.
    if (table) {
        try {
            const inside = TextSelection.near(editor.state.doc.resolve(table.pos + 3));
            editor.view.dispatch(editor.state.tr.setSelection(inside));
        } catch { /* an unusual table shape is not worth failing the insert over */ }
    }

    const chain = editor.chain().focus().addRowBefore();
    (already ? chain : chain.toggleHeaderRow()).run();
}
